// modal/detail/panels/ProgramPanel.jsx
import React, { useMemo, useEffect, useState, useCallback } from 'react';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import Icon from '@mui/material/Icon';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import { SoftCard } from 'shared/ui/soft/SoftCard';
import { PillIcon } from 'shared/ui/soft/PillIcon';

import { sessionsApi } from 'sectors/pilates/sessions/api/sessionsApi';

function hmFromMin(min) {
  const v = Number(min);
  if (!Number.isFinite(v)) return '—';
  const hh = String(Math.floor(v / 60)).padStart(2, '0');
  const mm = String(v % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

const DAY_TR = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cts', 'Paz'];
function dayLabelTR(dayISO) {
  if (!dayISO) return '—';
  const d = new Date(`${dayISO}T00:00:00`);
  const idx = (d.getDay() + 6) % 7; // Mon=0
  return DAY_TR[idx] || '—';
}

function isDebtPositive(x) {
  const v = Number(x);
  return Number.isFinite(v) && v > 0; // ✅ 0 kırmızı değil
}

function joinNames(xs, maxChars = 38) {
  const s = (xs || []).filter(Boolean).join(', ');
  if (s.length <= maxChars) return s;
  return `${s.slice(0, maxChars - 1)}…`;
}

function countAttendance(parts = []) {
  const up = (x) => String(x || '').toUpperCase();
  return parts.reduce(
    (acc, p) => {
      const st = up(p?.attendanceStatus);
      if (st === 'ATTENDED') acc.attended += 1;
      else if (st === 'NO_SHOW') acc.noShow += 1;
      else if (st === 'CANCELLED') acc.cancelled += 1;
      else acc.pending += 1;
      return acc;
    },
    { attended: 0, noShow: 0, cancelled: 0, pending: 0 }
  );
}

export function ProgramPanel({
  active,

  // ✅ dışarıdan verilirse onu gösterir, verilmezse içeride fetch eder
  weeklySessions: weeklySessionsProp,

  // ✅ fetch için
  trainerId, // eğitmen id (OWN scope ise backend zaten kendisine çeker)
  day, // opsiyonel: week anchor dayISO (2026-01-19 gibi)
  status, // opsiyonel: APPROVED vs

  // optional: (session) => void
  onSessionClick,
}) {
  const theme = useMuiTheme();
  const isDark = theme.palette.mode === 'dark';

  const [weeklySessionsState, setWeeklySessionsState] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // accordion
  const [openIds, setOpenIds] = useState({});
  const toggle = useCallback((id) => {
    if (id == null) return;
    setOpenIds((p) => ({ ...p, [id]: !p[id] }));
  }, []);

  const weeklySessions = Array.isArray(weeklySessionsProp)
    ? weeklySessionsProp
    : weeklySessionsState;

  // ✅ içeride fetch (prop gelmediyse)
  // ✅ içeride fetch (prop gelmediyse)
  useEffect(() => {
    let alive = true;
    const useProp = Array.isArray(weeklySessionsProp);
    if (!active) return;
    if (useProp) return;

    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const data = await sessionsApi.listWeekly({
          day,
          trainerId, // ✅ staff id
          status,
        });

        if (!alive) return;
        setWeeklySessionsState(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!alive) return;
        setWeeklySessionsState([]);
        setErr('Haftalık program yüklenemedi.');
        console.error('[ProgramPanel] listWeekly failed', e);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [active, day, trainerId, status, weeklySessionsProp]);

  const grouped = useMemo(() => {
    const arr = Array.isArray(weeklySessions) ? weeklySessions : [];
    const map = new Map(); // dayISO -> sessions[]
    for (const s of arr) {
      const dayISO = s?.dayISO || '—';
      if (!map.has(dayISO)) map.set(dayISO, []);
      map.get(dayISO).push(s);
    }

    const days = Array.from(map.keys()).sort((a, b) => String(a).localeCompare(String(b)));

    return days.map((dayISO) => {
      const sessions = (map.get(dayISO) || [])
        .slice()
        .sort((a, b) => Number(a?.startMin ?? 0) - Number(b?.startMin ?? 0));
      return { dayISO, sessions };
    });
  }, [weeklySessions]);

  if (!active) return null;

  const totalCount = Array.isArray(weeklySessions) ? weeklySessions.length : 0;

  return (
    <SoftCard>
      {/* Header */}
      <MDBox display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1.0 }}>
        <MDBox display="flex" alignItems="center" gap={1}>
          <PillIcon icon="calendar_month" tone="teal" />
          <MDBox>
            <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
              Haftalık Program
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
              Seanslar • katılımcılar • uyarılar
            </MDTypography>
          </MDBox>
        </MDBox>

        <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
          {loading ? (
            <MDBox sx={{ display: 'grid', placeItems: 'center', width: 28, height: 28 }}>
              <CircularProgress size={18} />
            </MDBox>
          ) : null}

          <Chip
            size="small"
            label={`${totalCount} seans`}
            variant="outlined"
            sx={{ borderRadius: '999px', fontWeight: 900 }}
          />
        </MDBox>
      </MDBox>

      {err ? (
        <MDTypography variant="caption" sx={{ color: 'error.main', fontWeight: 800, mb: 1 }}>
          {err}
        </MDTypography>
      ) : null}

      {/* Days */}
      <MDBox
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 1.05,
        }}
      >
        {grouped.length === 0 ? (
          <MDBox
            sx={{
              p: 1.1,
              borderRadius: '16px',
              border: `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.14 : 0.12)}`,
              bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#fff', 0.9),
            }}
          >
            <MDTypography variant="button" sx={{ opacity: 0.7, fontWeight: 800 }}>
              Seans yok
            </MDTypography>
          </MDBox>
        ) : (
          grouped.map(({ dayISO, sessions }) => {
            const dayLabel = dayLabelTR(dayISO);

            return (
              <MDBox
                key={dayISO}
                sx={{
                  p: 1.1,
                  borderRadius: '16px',
                  border: `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.14 : 0.12)}`,
                  bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#fff', 0.9),
                }}
              >
                {/* Day header */}
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 0.8 }}
                >
                  <MDBox sx={{ display: 'grid', gap: 0.1 }}>
                    <MDTypography variant="button" fontWeight="bold">
                      {dayLabel}
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ opacity: 0.7, fontWeight: 800 }}>
                      {dayISO}
                    </MDTypography>
                  </MDBox>

                  <Chip
                    size="small"
                    label={`${sessions.length} seans`}
                    variant="outlined"
                    sx={{ borderRadius: '999px', fontWeight: 900 }}
                  />
                </MDBox>

                {sessions.length === 0 ? (
                  <MDTypography variant="button" sx={{ opacity: 0.6 }}>
                    Seans yok
                  </MDTypography>
                ) : (
                  <MDBox sx={{ display: 'grid', gap: 0.55 }}>
                    {sessions.map((s) => {
                      const sid = s?.id;
                      const expanded = Boolean(openIds[sid]);

                      const time = hmFromMin(s?.startMin);
                      const activity = s?.activityName || 'Seans';

                      const parts = Array.isArray(s?.participants) ? s.participants : [];
                      const names = joinNames(parts.map((p) => p?.fullName).filter(Boolean), 40);

                      const hasNote = Boolean((s?.note || '').trim());
                      const noteText = (s?.note || '').trim();

                      const debtors = parts
                        .map((p) => ({ name: p?.fullName, debtTry: p?.debtTry }))
                        .filter((x) => x?.name && isDebtPositive(x?.debtTry));

                      const hasDebt = debtors.length > 0;

                      const debtTip = hasDebt
                        ? debtors
                            .map(
                              (d) => `• ${d.name}: ₺${Number(d.debtTry).toLocaleString('tr-TR')}`
                            )
                            .join('\n')
                        : '';

                      const cnt = countAttendance(parts);
                      const attSummary =
                        parts.length > 0
                          ? `${cnt.attended}/${parts.length} geldi${
                              cnt.noShow ? ` • ${cnt.noShow} gelmedi` : ''
                            }${cnt.pending ? ` • ${cnt.pending} beklemede` : ''}${
                              cnt.cancelled ? ` • ${cnt.cancelled} iptal` : ''
                            }`
                          : 'Katılımcı yok';

                      const clickable = Boolean(onSessionClick);

                      return (
                        <MDBox key={sid ?? `${dayISO}-${time}`}>
                          {/* Row */}
                          <MDBox
                            onClick={clickable ? () => onSessionClick(s) : undefined}
                            sx={{
                              p: 0.75,
                              borderRadius: '14px',
                              border: `1px solid ${alpha(
                                theme.palette.text.primary,
                                isDark ? 0.12 : 0.1
                              )}`,
                              bgcolor: isDark ? alpha('#0b1220', 0.14) : alpha('#0b1220', 0.02),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 1,
                              cursor: clickable ? 'pointer' : 'default',
                              transition: 'background .12s ease, border-color .12s ease',
                              '&:hover': clickable
                                ? {
                                    bgcolor: isDark
                                      ? alpha('#0b1220', 0.22)
                                      : alpha('#0b1220', 0.04),
                                    borderColor: alpha(theme.palette.info.main, 0.22),
                                  }
                                : undefined,
                            }}
                          >
                            {/* Left */}
                            <MDBox sx={{ minWidth: 0 }}>
                              <MDTypography
                                variant="button"
                                sx={{ fontWeight: 900, lineHeight: 1.15 }}
                                noWrap
                              >
                                {time}{' '}
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  sx={{ opacity: 0.75, fontWeight: 900 }}
                                >
                                  {activity}
                                </MDTypography>
                              </MDTypography>

                              <MDTypography
                                variant="caption"
                                sx={{ opacity: 0.8, fontWeight: 800 }}
                                noWrap
                              >
                                {names || 'Katılımcı yok'}
                              </MDTypography>
                            </MDBox>

                            {/* Right icons */}
                            <MDBox
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.55,
                                flex: '0 0 auto',
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {/* Attendance summary tooltip */}
                              <Tooltip title={attSummary} arrow placement="top">
                                <MDBox
                                  sx={{
                                    width: 30,
                                    height: 30,
                                    borderRadius: '12px',
                                    display: 'grid',
                                    placeItems: 'center',
                                    bgcolor: alpha(theme.palette.info.main, isDark ? 0.16 : 0.1),
                                    border: `1px solid ${alpha(theme.palette.info.main, 0.22)}`,
                                  }}
                                >
                                  <Icon sx={{ fontSize: 18, color: theme.palette.info.main }}>
                                    fact_check
                                  </Icon>
                                </MDBox>
                              </Tooltip>

                              {hasDebt ? (
                                <Tooltip
                                  title={
                                    <span style={{ whiteSpace: 'pre-line' }}>
                                      Borç var:
                                      {'\n'}
                                      {debtTip}
                                    </span>
                                  }
                                  arrow
                                  placement="top"
                                >
                                  <MDBox
                                    sx={{
                                      width: 30,
                                      height: 30,
                                      borderRadius: '12px',
                                      display: 'grid',
                                      placeItems: 'center',
                                      bgcolor: alpha(
                                        theme.palette.error.main,
                                        isDark ? 0.18 : 0.12
                                      ),
                                      border: `1px solid ${alpha(theme.palette.error.main, 0.22)}`,
                                    }}
                                  >
                                    <Icon sx={{ fontSize: 18, color: theme.palette.error.main }}>
                                      payments
                                    </Icon>
                                  </MDBox>
                                </Tooltip>
                              ) : null}

                              {hasNote ? (
                                <Tooltip title={noteText} arrow placement="top">
                                  <MDBox
                                    sx={{
                                      width: 30,
                                      height: 30,
                                      borderRadius: '12px',
                                      display: 'grid',
                                      placeItems: 'center',
                                      bgcolor: alpha(
                                        theme.palette.warning.main,
                                        isDark ? 0.16 : 0.12
                                      ),
                                      border: `1px solid ${alpha(
                                        theme.palette.warning.main,
                                        0.22
                                      )}`,
                                    }}
                                  >
                                    <Icon sx={{ fontSize: 18, color: theme.palette.warning.main }}>
                                      error_outline
                                    </Icon>
                                  </MDBox>
                                </Tooltip>
                              ) : null}

                              {/* Accordion toggle */}
                              <IconButton
                                size="small"
                                onClick={() => toggle(sid)}
                                sx={{
                                  width: 30,
                                  height: 30,
                                  borderRadius: '12px',
                                  border: `1px solid ${alpha(
                                    theme.palette.text.primary,
                                    isDark ? 0.12 : 0.1
                                  )}`,
                                }}
                              >
                                <Icon sx={{ fontSize: 18, opacity: 0.85 }}>
                                  {expanded ? 'expand_less' : 'expand_more'}
                                </Icon>
                              </IconButton>
                            </MDBox>
                          </MDBox>

                          {/* Accordion body */}
                          <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <MDBox
                              sx={{
                                mt: 0.55,
                                p: 0.75,
                                borderRadius: '14px',
                                border: `1px dashed ${alpha(
                                  theme.palette.text.primary,
                                  isDark ? 0.16 : 0.14
                                )}`,
                                bgcolor: isDark ? alpha('#0b1220', 0.12) : alpha('#0b1220', 0.015),
                                display: 'grid',
                                gap: 0.55,
                              }}
                            >
                              <MDTypography
                                variant="caption"
                                sx={{ fontWeight: 900, opacity: 0.85 }}
                              >
                                Yoklama: {attSummary}
                              </MDTypography>

                              {hasDebt ? (
                                <MDTypography
                                  variant="caption"
                                  sx={{ fontWeight: 900, color: 'error.main' }}
                                >
                                  Borçlular:{' '}
                                  {debtors
                                    .map(
                                      (d) =>
                                        `${d.name} ₺${Number(d.debtTry).toLocaleString('tr-TR')}`
                                    )
                                    .join(' • ')}
                                </MDTypography>
                              ) : (
                                <MDTypography
                                  variant="caption"
                                  sx={{ fontWeight: 900, opacity: 0.7 }}
                                >
                                  Borç: yok
                                </MDTypography>
                              )}

                              {hasNote ? (
                                <MDTypography
                                  variant="caption"
                                  sx={{ fontWeight: 900, opacity: 0.85 }}
                                >
                                  Not: {noteText}
                                </MDTypography>
                              ) : (
                                <MDTypography
                                  variant="caption"
                                  sx={{ fontWeight: 900, opacity: 0.7 }}
                                >
                                  Not: yok
                                </MDTypography>
                              )}

                              {/* participants list (net görmek için) */}
                              {parts.length ? (
                                <MDBox sx={{ display: 'grid', gap: 0.35 }}>
                                  {parts.map((p) => {
                                    const st = String(p?.attendanceStatus || 'PENDING');
                                    const debt = isDebtPositive(p?.debtTry)
                                      ? ` • ₺${Number(p.debtTry).toLocaleString('tr-TR')} borç`
                                      : '';
                                    return (
                                      <MDTypography
                                        key={p?.id ?? `${sid}-${p?.memberId}-${p?.fullName}`}
                                        variant="caption"
                                        sx={{ fontWeight: 800, opacity: 0.85 }}
                                        noWrap
                                      >
                                        • {p?.fullName || '—'} — {st}
                                        {debt}
                                      </MDTypography>
                                    );
                                  })}
                                </MDBox>
                              ) : null}
                            </MDBox>
                          </Collapse>
                        </MDBox>
                      );
                    })}
                  </MDBox>
                )}
              </MDBox>
            );
          })
        )}
      </MDBox>
    </SoftCard>
  );
}
